<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { UiText } from "@shopexperts/shared";

import { partnerDirectoryAvatarSources, type PartnerDirectoryAvatarInput } from "@/app/services/partnerDirectoryRecordAvatar";

// Sizing comes from the parent through class fallthrough on the root box.
const props = defineProps<{
  record: PartnerDirectoryAvatarInput;
  alt?: string;
  contain?: boolean;
}>();

const avatar = computed(() => partnerDirectoryAvatarSources(props.record));
const candidateIndex = ref(0);

// Restart the walk when the candidate list changes (record swap, or a fresh
// crawl upload landing on the selected record).
watch(
  () => avatar.value.sources.join("|"),
  () => {
    candidateIndex.value = 0;
  },
);

const currentSource = computed(() => avatar.value.sources[candidateIndex.value] ?? null);

// Scraped Shopify URLs expire; when one fails to load walk to the next
// candidate and finally to the initials block, so a card never renders blank.
function onImageError() {
  if (candidateIndex.value < avatar.value.sources.length) candidateIndex.value += 1;
}
</script>

<template>
  <div class="shrink-0 overflow-hidden rounded-md border border-se-gray-200 bg-white">
    <img
      v-if="currentSource"
      :key="currentSource"
      :src="currentSource"
      :alt="props.alt ?? avatar.name"
      class="block h-full w-full"
      :class="props.contain ? 'object-contain' : 'object-cover'"
      loading="lazy"
      decoding="async"
      @error="onImageError"
    />
    <div v-else class="flex h-full w-full items-center justify-center bg-se-secondary">
      <UiText as="span" variant="caption-normal" class="font-semibold text-se-gray-700" :text="avatar.initials" />
    </div>
  </div>
</template>
